import { useQueryClient } from '@tanstack/react-query'
import { formatISO } from 'date-fns'
import { useCallback } from 'react'
import {
  globalQueryKeys,
  useCreateBulkMomentsToPlanRequest,
} from '@opallabs/api'
import { readPlan } from '@opallabs/api/BFF'
import { useWorkspace } from '@opallabs/hooks'
import { toast } from '@opallabs/lapidary'
import {
  BULK_SYNCHRONOUS_LIMIT,
  defaultBulkToastStartOptions,
  StartAsyncBulkUpdateToast,
  StartSyncBulkUpdateToast,
} from '../../BulkUpdate/BulkUpdateToasts'
import {
  OnAddToPlanArgs,
  OnAddToPlanSuccessCallback,
  SelectionMeta,
} from '../types'

export const useOnAddToPlan = ({
  onSuccess,
}: {
  onSuccess: OnAddToPlanSuccessCallback
}) => {
  const { uuid: workspaceId } = useWorkspace()
  const queryClient = useQueryClient()
  const createBulkUpdate = useCreateBulkMomentsToPlanRequest<SelectionMeta>({
    onMutate: ({ moments, selectionMeta }) => {
      const invalidMomentCount = selectionMeta?.invalidMomentCount ?? 0
      // Show bulk update start toast if multiple moments have been selected,
      // even if only one moment is valid
      if (moments.length + invalidMomentCount > 1) {
        if (moments.length > BULK_SYNCHRONOUS_LIMIT) {
          return toast(
            (props) => <StartAsyncBulkUpdateToast {...props} />,
            defaultBulkToastStartOptions
          )
        }

        toast(
          (props) => <StartSyncBulkUpdateToast {...props} />,
          defaultBulkToastStartOptions
        )
      }
    },
    onSuccess: (response, { selectionMeta }) => {
      // If we don't have a response, the bulk operation is being
      // processed aync and we should not send any additional toasts
      // or try to refetch data
      if (!response) return

      if (response.data.successes.length) {
        // All moments in successes have same plan id as related resource
        const planId = response.data.successes[0].related_resource_id

        readPlan.invalidateCache(queryClient, {
          workspaceUuid: workspaceId,
          resourceId: planId,
          queryParams: null,
        })

        // Invalidate paired block queries so placement status stays current
        selectionMeta?.moments.forEach((moment) => {
          if (moment.pairedBlockId) {
            queryClient.invalidateQueries(
              globalQueryKeys.blocksByWorkspace(workspaceId)
            )
          }
        })
      }

      // Invalidate moment queries so paired_block relationship is fresh
      // on next modal open
      selectionMeta?.moments.forEach((moment) => {
        queryClient.invalidateQueries(globalQueryKeys.momentById(moment.id))
      })

      onSuccess?.(response.data, selectionMeta)
    },
  })

  const onAddToPlan = useCallback(
    async ({
      invalidMomentCount,
      moments,
      selectedCategoryType,
      selectedPlan,
      selectedSwimlane,
    }: OnAddToPlanArgs) => {
      createBulkUpdate.mutate({
        moments: moments.map((moment) => ({
          id: moment.id,
          title: moment.title,
          start_at: moment.schedule?.startDate
            ? formatISO(moment.schedule.startDate, { representation: 'date' })
            : null,
          end_at: moment.schedule?.endDate
            ? formatISO(moment.schedule.endDate, { representation: 'date' })
            : null,
        })),
        workspaceId: workspaceId,
        planId: selectedPlan.id,
        categoryId: selectedSwimlane?.value,
        categoryTypeId: selectedCategoryType.value,
        selectionMeta: {
          invalidMomentCount,
          moments,
          plan: selectedPlan,
        },
      })
    },
    [createBulkUpdate, workspaceId]
  )

  return onAddToPlan
}
